import {
  CATEGORY_SPREAD,
  MARKET_ITEMS,
  isFixedPriceEquipment,
  itemPriceIndex,
  marketPricePerUnit,
  type MarketCategory
} from "../data/marketItems";
import { RESOURCE_TIER } from "../resourceCatalog";

/**
 * The per-station market (design doc 16.92).
 *
 * Every price and every stock level here is DERIVED from a hash of
 * (stationId, itemName, epoch) rather than stored. That is the central
 * decision, and it is driven by Section 13's local-first-plus-cloud-sync
 * model: 7 stations x ~62 items is 434 prices, and storing that table would
 * both add save bytes and let two devices disagree about prices, forcing
 * conflict resolution to pick a winner. Derived values are byte-identical on
 * every device and after every reload, need no scheduler, no background job
 * and no server -- "the market refreshed" is just the epoch index advancing.
 *
 * Only the player's own trades are stored, as deltas against the derived
 * stock, keyed by epoch so they self-clean (see consumedKey/staleness in
 * ui/state.ts).
 */

/**
 * One hour, per Don's ruling (revising his initial 24h).
 *
 * 24 hours means the market never moves during a session: no reason to
 * re-check a station, and the Dope Wars beat -- tick, prices move,
 * re-evaluate -- never happens while anyone is playing. A haul is 5-15
 * minutes, so an hour gives 4-12 hauls per tick: long enough to plan and
 * execute a run without prices shifting under the captain, short enough that a
 * two-hour session sees the market move twice.
 *
 * Real time, not game time, deliberately: the game clock pauses on docking and
 * at the Menu, so a game-time epoch could be frozen indefinitely while
 * shopping.
 */
export const MARKET_EPOCH_MS = 60 * 60 * 1000;

export function currentMarketEpoch(nowMs: number): number {
  return Math.floor(nowMs / MARKET_EPOCH_MS);
}

/** Milliseconds until the market next moves -- for the screen's countdown. */
export function msUntilNextEpoch(nowMs: number): number {
  return MARKET_EPOCH_MS - (nowMs % MARKET_EPOCH_MS);
}

/**
 * FNV-1a over the joined parts. Chosen for the same reason itemPriceIndex uses
 * it: the result has to be identical across builds, platforms and reloads, so
 * anything touching Math.random or object iteration order is disqualified.
 */
function hash32(...parts: Array<string | number>): number {
  const s = parts.join(" ");
  let hash = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    hash ^= s.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return fmix32(hash);
}

/**
 * MurmurHash3's finalizer, and not decoration.
 *
 * FNV-1a alone distributes poorly over short, near-identical keys, and every
 * key here differs by only a few characters ("price S001 Fuel 490123" versus
 * "price S007 Fuel 490123"). Measured against the real 7 stations x 49
 * non-Provisions items, raw FNV came out 20%-31% stocked against a 25% target
 * depending on which bits were sampled -- a genuine skew, not sampling noise.
 *
 * It matters far beyond stock counts, which is why it is worth the four lines:
 * the same function drives stationPriceIndex, so a biased hash leans the whole
 * market toward one end of the 0.70-1.40 range and makes every hauling profit
 * systematically wrong in the same direction.
 */
function fmix32(h: number): number {
  let x = h >>> 0;
  x ^= x >>> 16;
  x = Math.imul(x, 0x85ebca6b) >>> 0;
  x ^= x >>> 13;
  x = Math.imul(x, 0xc2b2ae35) >>> 0;
  x ^= x >>> 16;
  return x >>> 0;
}

/** A stable pseudo-random in [0, 1) from any key, using the full 32-bit width. */
function unit(...parts: Array<string | number>): number {
  return hash32(...parts) / 4294967296;
}

// ---------------------------------------------------------------------------
// The four multipliers (16.92). categoryBase and itemIndex live in
// data/marketItems.ts; the two here are the station's own contribution.
// ---------------------------------------------------------------------------

/**
 * The hourly, per-station, per-item swing: 0.70 to 1.40.
 *
 * This is the Dope Wars engine -- the entire reason to carry something from
 * one station to another. Range chosen against the spread: a haul profits when
 * `sell_index x (1 - spread) > buy_index`, so at a 20% headline spread the
 * best pair (1.40 vs 0.70) returns 70% and a merely-good pair still clears,
 * which is the "profession and a hunt" balance Don asked for.
 */
export const STATION_INDEX_MIN = 0.7;
export const STATION_INDEX_MAX = 1.4;

export function stationPriceIndex(stationId: string, itemName: string, epoch: number): number {
  const span = STATION_INDEX_MAX - STATION_INDEX_MIN;
  return STATION_INDEX_MIN + unit("price", stationId, itemName, epoch) * span;
}

export type GalaxyRegion = "safe" | "neutral" | "crimson" | "azure" | "solthane" | "pirate";

/**
 * Permanent regional bias: what a place is systematically dear or cheap in.
 *
 * Fixed, never re-rolled, and that is the point. Pure hourly randomness gives
 * a player nothing to learn -- every run would be a lottery. A standing bias
 * means there are routes worth KNOWING (haul Tech out of faction space, sell
 * Medical on the fringe), and the hourly index only decides whether today is a
 * good day to run one. Anything unlisted is 1.0.
 */
const REGION_BIAS: Record<GalaxyRegion, Partial<Record<MarketCategory, number>>> = {
  // Core worlds: rich collectors, well-fed, and no appetite for risk.
  safe: { Artifact: 1.15, Luxury: 1.12, Provisions: 0.88, Salvage: 0.9 },
  neutral: {},
  // The three faction powers manufacture, so their own output is cheap at home
  // and the things they cannot make locally cost more.
  crimson: { Munitions: 0.88, Tech: 0.95, Luxury: 1.1, Medical: 1.05 },
  azure: { Tech: 0.86, Medical: 0.92, Luxury: 1.08, Munitions: 1.05 },
  solthane: { Industrial: 0.85, Medical: 0.88, Tech: 1.08, Artifact: 1.1 },
  // The fringe: awash in salvage and contraband, desperate for anything
  // civilised. This is the shape that makes a pirate-aligned hauler a real
  // profession rather than a worse version of a legitimate one.
  pirate: { Contraband: 0.82, Salvage: 0.85, Provisions: 1.18, Medical: 1.2, Tech: 1.12 }
};

export function regionBias(region: GalaxyRegion, itemName: string): number {
  const category = categoryOf(itemName);
  if (!category) return 1;
  return REGION_BIAS[region]?.[category] ?? 1;
}

function categoryOf(itemName: string): MarketCategory | null {
  return MARKET_ITEMS.find((i) => i.name === itemName)?.category ?? null;
}

// ---------------------------------------------------------------------------
// Stock
// ---------------------------------------------------------------------------

/**
 * Fraction of the catalogue any one station stocks, per Don's ruling (down
 * from the 60% first proposed). Scarcity is what makes a route worth knowing:
 * if everything is available everywhere, there is nothing to go and fetch.
 */
export const STATION_STOCK_FRACTION = 0.25;

/**
 * Provisions -- which is the category Fuel itself lives in -- is always
 * stocked everywhere (Don's ruling). A captain must never be stranded unable
 * to buy fuel or feed their crew because of a dice roll.
 */
function alwaysStocked(itemName: string): boolean {
  // Drones join Provisions here (16.94): Don's ruling puts them in the same
  // economic bracket as fuel and rations -- a part you must be able to replace,
  // not a bet you place. A captain who lost their drones two jumps out should
  // never find the only station in reach has none.
  return categoryOf(itemName) === "Provisions" || isFixedPriceEquipment(itemName);
}

export function stationStocks(stationId: string, itemName: string, epoch: number): boolean {
  if (alwaysStocked(itemName)) return true;
  return unit("stock", stationId, itemName, epoch) < STATION_STOCK_FRACTION;
}

/**
 * How many units a station has, before the player buys any.
 *
 * Banded by value tier, so scarcity tracks worth: bulk staples arrive by the
 * hundred, Artifacts by the handful. A station holding 400 Xenite Alloy would
 * make the rarest tier in the game feel like gravel.
 */
/**
 * The largest hold in the game -- a T4 Packmule, 1,568 m3. The anchor for
 * every number below, because Don's requirement is narrative: a single capital
 * hauler turning up must not be able to strip a station bare. A market that one
 * ship can empty is not a market, it is a vending machine.
 */
export const CAPITAL_HOLD_M3 = 1568;

/**
 * Units in stock, by value tier, for a HUB station.
 *
 * Common goods start above a capital hold on purpose: even at the bottom of
 * the band, a T4 Packmule takes at most about half the Provisions on the
 * shelf. Scarcity still tracks value -- Artifacts stay in the hundreds, and a
 * capital hauler CAN clear a station's entire Artifact stock, which is the
 * right outcome: there are only so many alien relics, and a big ship arriving
 * to take all of them is a story rather than a bug.
 */
const HUB_STOCK_BANDS: Record<number, [number, number]> = {
  0: [3200, 9000], // Provisions, Industrial -- 2x to 6x a capital hold
  1: [1800, 5200], // Salvage
  2: [800, 2400], // Medical, Munitions, Tech
  3: [300, 900], // Luxury, Contraband
  4: [110, 380] // Artifact
};

/**
 * A frontier outpost carries roughly a fifth of a hub's depth.
 *
 * This is the number that gives hauling a destination rather than only a
 * source. An outpost that stocked like a hub would have nothing to want, and
 * the whole trade loop would collapse into moving goods between equals.
 */
const FRONTIER_STOCK_DIVISOR = 5;

export type StationClass = "hub" | "frontier";

const CATEGORY_TIER_FOR_STOCK: Record<MarketCategory, number> = {
  Provisions: 0,
  Industrial: 0,
  Salvage: 1,
  Medical: 2,
  Munitions: 2,
  Tech: 2,
  Luxury: 3,
  Contraband: 3,
  Artifact: 4
};

export function baseStockQty(
  stationId: string,
  itemName: string,
  epoch: number,
  stationClass: StationClass = "hub"
): number {
  if (!stationStocks(stationId, itemName, epoch)) return 0;
  const category = categoryOf(itemName);
  const tier = category ? CATEGORY_TIER_FOR_STOCK[category] : (RESOURCE_TIER[itemName] ?? 2);
  const [lo, hi] = HUB_STOCK_BANDS[tier] ?? HUB_STOCK_BANDS[2];
  const hubQty = lo + unit("qty", stationId, itemName, epoch) * (hi - lo);
  const qty = stationClass === "frontier" ? hubQty / FRONTIER_STOCK_DIVISOR : hubQty;
  // Floor of 1: an outpost that stocks something must have at least one of it,
  // or the row shows as available and buys nothing.
  return Math.max(1, Math.round(qty));
}

// ---------------------------------------------------------------------------
// Prices
// ---------------------------------------------------------------------------

/**
 * Equipment, not a commodity (Don's ruling, 16.94): a fixed price, identical
 * everywhere, and identical on both sides of the counter.
 *
 * Drones are munitions the captain fits and loses, priced at 40% of the hull
 * they serve (16.87) -- which meant 16.92's 100x hull reprice carried them with
 * it, and a Capital Drone became by far the most profitable thing in the galaxy
 * to haul: ~417 CR/m3 against 20-30 for an Artifact, so a single capital hold
 * cleared ~646,000 CR a run. Not a pricing bug so much as a category error, and
 * the fix is to stop treating a fitted part as speculative cargo.
 *
 * Flat and equal removes the arbitrage by construction rather than by
 * balancing: with no spread and no station variation there is no trade to make,
 * only a part to buy. Buying and reselling at the same price also means a
 * captain who fits the wrong drone loses nothing but time, which is how an
 * equipment store should behave.
 */
/** What this station charges for one unit right now. */
export function stationBuyPrice(stationId: string, region: GalaxyRegion, itemName: string, epoch: number): number {
  if (isFixedPriceEquipment(itemName)) return marketPricePerUnit(itemName);
  return (
    marketPricePerUnit(itemName) *
    itemPriceIndex(itemName) *
    stationPriceIndex(stationId, itemName, epoch) *
    regionBias(region, itemName)
  );
}

function spreadFor(itemName: string): number {
  if (RESOURCE_TIER[itemName] !== undefined) return 0.1;
  const category = categoryOf(itemName);
  return category ? CATEGORY_SPREAD[category] : 0.15;
}

/**
 * What this station pays for one unit right now. Always strictly below its own
 * buy price -- the spread is what stops any single station being a money loop.
 *
 * `quantity` is accepted and deliberately ignored: Don ruled that order size
 * must not move prices for now, but the signature is in place so Port
 * Royale-style price impact is a change of one function body later rather than
 * a change at every call site.
 */
export function stationSellPrice(
  stationId: string,
  region: GalaxyRegion,
  itemName: string,
  epoch: number,
  _quantity = 1
): number {
  // Equipment sells back for exactly what it cost (16.94) -- no spread, so
  // there is nothing to arbitrage and no penalty for changing your mind.
  if (isFixedPriceEquipment(itemName)) return marketPricePerUnit(itemName);
  return stationBuyPrice(stationId, region, itemName, epoch) * (1 - spreadFor(itemName));
}

/**
 * Contraband can only be sold in pirate regions (Don's ruling) -- the rule
 * that gives a pirate-aligned hauler their own trade.
 *
 * ENABLED as of 16.93. It was written inert in 16.92 because `pirate` was a
 * real region with no station in it, so enforcing the rule would have made
 * Contraband dead cargo -- lootable and buyable but impossible to convert. The
 * 21-station expansion put 8 stations in pirate space, which is precisely what
 * this was waiting for, so Contraband now has exactly one kind of buyer and
 * pirate alignment has a trade of its own.
 */
export const CONTRABAND_PIRATE_ONLY_ENFORCED = true;

export function canSellHere(itemName: string, region: GalaxyRegion): boolean {
  if (!CONTRABAND_PIRATE_ONLY_ENFORCED) return true;
  if (categoryOf(itemName) !== "Contraband") return true;
  return region === "pirate";
}

/**
 * Stations buy anything, whether or not they stock it (Don's ruling) -- so a
 * captain can always liquidate a hold. Stocking only governs what is for sale.
 */
export function stationBuysAnything(): true {
  return true;
}

// ---------------------------------------------------------------------------
// Hauling profit -- what the hauler's sort column is built on
// ---------------------------------------------------------------------------

export interface HaulProspect {
  itemName: string;
  bestStationId: string;
  profitPerUnit: number;
}

/**
 * The best profit per unit available for an item, buying here and selling at
 * whichever other station pays most.
 *
 * This is the hauler's equivalent of showing a combat pilot the payout and hop
 * count before they accept (Don's item 9). Negative means no station currently
 * pays enough to cover this station's asking price plus the spread -- which is
 * real information, not a gap: most goods are not worth hauling most hours,
 * and knowing which is the whole skill.
 */
export function bestHaulProfit(
  itemName: string,
  fromStationId: string,
  fromRegion: GalaxyRegion,
  otherStations: Array<{ id: string; region: GalaxyRegion }>,
  epoch: number
): HaulProspect | null {
  const buy = stationBuyPrice(fromStationId, fromRegion, itemName, epoch);
  let best: HaulProspect | null = null;
  otherStations.forEach((station) => {
    if (station.id === fromStationId) return;
    if (!canSellHere(itemName, station.region)) return;
    const profit = stationSellPrice(station.id, station.region, itemName, epoch) - buy;
    if (!best || profit > best.profitPerUnit) {
      best = { itemName, bestStationId: station.id, profitPerUnit: profit };
    }
  });
  return best;
}

/**
 * Where in the galaxy this item fetches the most, in absolute credits per unit
 * (16.98).
 *
 * `bestHaulProfit` above answers a different question -- it is relative to what
 * a particular station is *charging*, which only makes sense while standing at
 * a counter. The Cargo UI is available in deep space with no station in reach,
 * and the captain's question there is simply "where do I take this". So this
 * maximises the sell price outright and never subtracts a buy price.
 *
 * Contraband is respected: a station that cannot legally take it is not the best
 * place to sell it, however much it would pay.
 */
export function bestSellStation(
  itemName: string,
  stations: Array<{ id: string; region: GalaxyRegion }>,
  epoch: number
): { stationId: string; pricePerUnit: number } | null {
  let best: { stationId: string; pricePerUnit: number } | null = null;
  stations.forEach((station) => {
    if (!canSellHere(itemName, station.region)) return;
    const price = stationSellPrice(station.id, station.region, itemName, epoch);
    if (!best || price > best.pricePerUnit) best = { stationId: station.id, pricePerUnit: price };
  });
  return best;
}
